import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Brain, Database, Layers, RefreshCw } from 'lucide-react';
interface SlidePlatformArchitectureProps {
  onContinue: () => void;
}
export function SlidePlatformArchitecture({
  onContinue
}: SlidePlatformArchitectureProps) {
  const [stage, setStage] = useState(0);
  const layers = [{
    id: 'app',
    title: 'Application Suite',
    color: 'bg-teal-400',
    textColor: 'text-teal-400',
    icon: <Layers className="w-5 h-5" data-id="element-702" data-mp="1hhp8d64gjczn" />,
    chips: ['Snak Composer', 'Snakker Player', 'Distribution', 'Analytics']
  }, {
    id: 'ai',
    title: 'AI Intelligence Engine',
    color: 'bg-purple-500',
    textColor: 'text-purple-400',
    icon: <Brain className="w-5 h-5" data-id="element-703" data-mp="3o96mr18x1fcf" />,
    chips: ['Blueprint Analysis', 'Story Generation', 'Content Creation', 'Channel Optimization']
  }, {
    id: 'data',
    title: 'Data Supply Chain',
    color: 'bg-rose-500',
    textColor: 'text-rose-400',
    icon: <Database className="w-5 h-5" data-id="element-704" data-mp="1l4l7m3q8lyqm" />,
    chips: ['TA Themes', 'Brand Data', 'HCP Insights', 'Market Intel']
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 800);
    const timers = layers.map((_, idx) => setTimeout(() => setStage(3 + idx), 1300 + idx * 500));
    const final = setTimeout(() => setStage(3 + layers.length), 1300 + layers.length * 500 + 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      timers.forEach(clearTimeout);
      clearTimeout(final);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-8 md:px-20 md:py-10 max-w-7xl mx-auto" data-id="element-705" data-mp="1uuwfu2gyeu3b">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-706" data-mp="nhcjs22rkdlg">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-707" data-mp="kwo73yaawgg3">
          Platform Architecture
        </h2>
        <p className="text-xl text-white/60" data-id="element-708" data-mp="pug8rk0qzgxh">
          An AI-driven closed-loop system.
        </p>
      </motion.div>

      {/* Subheader chip */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} className="flex items-center gap-4 mt-5" data-id="element-709" data-mp="fwikwxmsvm5e">
        <span className="px-3 py-1 text-xs uppercase tracking-widest text-teal-300 bg-teal-400/10 border border-teal-400/30 rounded-full font-semibold" data-id="element-710" data-mp="6wvnemx6z0hm">
          Platform Architecture
        </span>
        <div className="flex items-center gap-2 text-white/40 text-sm italic" data-id="element-711" data-mp="3tbewc43edngp">
          <RefreshCw className="w-3.5 h-3.5" data-id="element-712" data-mp="3fc5mrn9p5rlp" />
          Think · Create · Learn · Improve
        </div>
      </motion.div>

      {/* Layered stack */}
      <div className="flex-1 flex flex-col gap-3 mt-5" data-id="element-713" data-mp="3jd8ypyomj3hn">
        {layers.map((layer, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 3 + idx ? 1 : 0,
        x: stage >= 3 + idx ? 0 : -20
      }} transition={{
        duration: 0.5
      }} className="bg-navy-900/60 border border-teal-400/20 rounded-2xl p-5 md:p-6" data-id="element-714" data-mp="30q9n02fzl3ny" data-mpi={idx}>
            <div className="flex items-start gap-4 mb-3" data-id="element-715" data-mp="3jlzmnj0g5nyo">
              <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/30 flex items-center justify-center text-teal-400 flex-shrink-0" data-id="element-716" data-mp="3eog9kmfhjh8b">
                {layer.icon}
              </div>
              <div className="flex-1 min-w-0" data-id="element-717" data-mp="3ogc81ylhh9d2">
                <h3 className="text-lg md:text-xl font-bold text-teal-300" data-id="element-718" data-mp="69ftyrxfm11h">
                  {layer.title}
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-id="element-719" data-mp="1c66vixim1gt">
              {layer.chips.map(chip => <div key={chip} className="bg-navy-800/80 border border-navy-600 rounded-lg px-3 py-2.5 text-center" data-id="element-720" data-mp="lamwwsl4ve07">
                  <span className="text-white/85 text-xs md:text-sm font-semibold" data-id="element-721" data-mp="u5b10fez2pnf">
                    {chip}
                  </span>
                </div>)}
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 3 + layers.length ? 1 : 0
    }} className="mt-5 flex justify-end" data-id="element-722" data-mp="6f6yychfmxyj"></motion.div>
    </div>;
}